import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * One guest-allowance config matching the authenticated user's active
 * memberships, with the derived consumption for the evaluated calendar year.
 */
export interface RemainingAllowanceConfig {
  configId: string;
  membershipTypeId: string;
  membershipTypeName: string | null;
  unitsPerCalendarYear: number;
  consumed: number;
  /** Remaining guest units (clamped at 0 for display) */
  remaining: number;
  /** Flat guest fee per hour in integer cents */
  guestFeePerHourCents: number;
}

export interface RemainingAllowanceResponse {
  /** Venue-local calendar year evaluated */
  year: number;
  /** Date the quota resets (Jan 1, venue-local), YYYY-MM-DD */
  resetDate: string;
  /**
   * Whether the guest allowance (fee + quota) APPLIES to the window passed to
   * `getRemaining` — true only for member-only windows, decided by the same
   * pricing analysis the backend charges with. `null` when no window was
   * passed; must not be read as "applies".
   */
  appliesToWindow: boolean | null;
  configs: RemainingAllowanceConfig[];
}

/** The window the member is about to book. */
export interface GuestAllowanceWindow {
  /** ISO-8601 instant of the booking start */
  startTime: string;
  durationMinutes: number;
}

/**
 * Member-facing guest allowance read (remaining guest units for member-only
 * bookings). Admin CRUD lives in AdminGuestAllowanceService.
 */
@Injectable({
  providedIn: 'root',
})
export class GuestAllowanceService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Remaining guest units of the AUTHENTICATED user for a category.
   * `date` (YYYY-MM-DD) selects the venue-local calendar year of the booking;
   * defaults to today. Pass `window` (the slot about to be booked) to get
   * `appliesToWindow` — without it the read is category-scoped and says
   * nothing about whether a fee/quota will apply to a particular slot.
   */
  getRemaining(
    organizationId: string,
    categoryId: string,
    date?: string,
    window?: GuestAllowanceWindow,
  ): Observable<RemainingAllowanceResponse> {
    let params = new HttpParams().set('categoryId', categoryId);
    if (date) {
      params = params.set('date', date);
    }
    if (window) {
      params = params
        .set('startTime', window.startTime)
        .set('durationMinutes', String(window.durationMinutes));
    }
    return this.http.get<RemainingAllowanceResponse>(
      `${this.apiUrl}/organizations/${organizationId}/guest-allowance/remaining`,
      { params },
    );
  }
}
